import { useState, useEffect, useRef, useContext, useCallback } from "react";
import DarkModeToggle from "../DarkModeToggle";
import AssistantLauncher from "../assistant/AssistantLauncher";
import { api, MainContext } from "../../AppContext";
import { FaBars, FaSignOutAlt, FaUser } from "react-icons/fa";

interface User {
  first_name: string;
  last_name: string;
  email?: string;
}

const Navbar = ({ isSidebarOpen, setIsSidebarOpen }: any) => {
  const { setToken, user, userLoaded } = useContext(MainContext) as {
    setToken: (token: string | null) => void;
    user: User;
    userLoaded: boolean;
  };
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const toggleButtonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleAvatarToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleClickOutside = useCallback((event: MouseEvent) => {
    const target = event.target as Node;
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(target) &&
      toggleButtonRef.current &&
      !toggleButtonRef.current.contains(target)
    ) {
      setIsSidebarOpen(false);
    }

    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(target) &&
      avatarButtonRef.current &&
      !avatarButtonRef.current.contains(target)
    ) {
      setIsDropdownOpen(false);
    }
  }, [setIsSidebarOpen]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  const signOut = () => {
    api.post("/logout").catch(() => undefined);
    api.defaults.headers.common["Authorization"] = "";
    localStorage.removeItem("token");
    setToken(null);
  };

  // Avoid showing incorrect user info before data is loaded
  if (!userLoaded) {
    return (
      <nav className="fixed top-0 z-50 right-0 left-0 sm:left-[var(--sidebar-offset,18rem)] h-16 bg-card text-card-foreground border-b border-border" />
    );
  }

  return (
    <nav className="fixed top-0 z-50 right-0 left-0 sm:left-[var(--sidebar-offset,18rem)] bg-card text-card-foreground border-b border-border transition-[left] duration-200">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          {/* Left section — only mobile toggle, brand lives in sidebar */}
          <div className="flex items-center gap-3 min-w-0">
            <button
              ref={toggleButtonRef}
              type="button"
              className="md:hidden flex items-center justify-center w-9 h-9 rounded-md bg-background text-foreground border border-input hover:bg-accent transition-colors"
              onClick={handleSidebarToggle}
              aria-label="Open sidebar"
            >
              <FaBars className="w-4 h-4" />
            </button>
          </div>

          {/* Right section */}
          <div className="flex items-center gap-2">
            <AssistantLauncher />
            <DarkModeToggle />

            {/* Profile dropdown */}
            <div className="relative">
              <button
                ref={avatarButtonRef}
                className="flex items-center justify-center w-9 h-9 rounded-md border border-input bg-background hover:bg-accent transition-colors focus:outline-none overflow-hidden"
                onClick={handleAvatarToggle}
                aria-label="Account menu"
              >
                <img
                  className="w-full h-full object-cover"
                  src={`https://ui-avatars.com/api/?name=${user.first_name}+${user.last_name}&background=000000&color=ffffff&bold=true`}
                  alt={`${user.first_name} ${user.last_name}`}
                />
              </button>

              {/* Dropdown menu */}
              <div
                ref={dropdownRef}
                className={`
                  absolute right-0 mt-2 w-64 origin-top-right rounded-md
                  bg-popover text-popover-foreground border border-border
                  shadow-lg focus:outline-none
                  transition-all duration-200
                  ${
                    isDropdownOpen
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-1 pointer-events-none"
                  }
                `}
              >
                {/* User Info */}
                <div className="px-3 py-3 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-muted border border-border flex items-center justify-center">
                      <FaUser className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {user.first_name} {user.last_name}
                      </p>
                      {user.email && (
                        <p className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="p-1">
                  <button
                    onClick={signOut}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-left text-sm text-foreground hover:bg-accent transition-colors"
                  >
                    <FaSignOutAlt className="w-3.5 h-3.5" />
                    <span>Sign out</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
